import axios from "axios";
import dayjs from "dayjs";
import { message } from "antd";

export const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const url = String(error.config?.url || "");
    const isLoginRequest = url.includes("/backend/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/";
    }
    // Read-only user attempted a write operation — surface the backend message globally
    const readOnlyMessage = error.response?.data?.error;
    if (
      error.response?.status === 403 &&
      typeof readOnlyMessage === "string" &&
      readOnlyMessage.toLowerCase().includes("read-only")
    ) {
      message.error(readOnlyMessage);
    }
    return Promise.reject(error);
  },
);

// Auth
export const login = async (data: { email: string; password: string }) => {
  try {
    const response = await API.post("/backend/login", data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const sendOtp = async (data: { email: string }) => {
  try {
    const response = await API.post("/backend/sendOtp", data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const verifyOtp = async (data: { email: string; otp: string }) => {
  try {
    const response = await API.post("/backend/verifyOtp", data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const resetPasswordWithOtp = async (data: {
  email: string;
  otp: string;
  newPassword: string;
}) => {
  try {
    const response = await API.put("/backend/resetPasswordWithOtp", data);
    return response;
  } catch (error) {
    throw error;
  }
};

// Profile (logged-in user)
export const getProfile = async () => {
  try {
    const response = await API.get("/backend/profile");
    return response;
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (data: { newPassword: string }) => {
  try {
    const response = await API.put("/backend/changePassword", data);
    return response;
  } catch (error) {
    throw error;
  }
};

// Customers
export const addCutomer = async (data: {
  customerName: string;
  userEmail: string;
  customerCode: number;
  band: string;
  join_date: string;
  contact: string;
  address: string;
  city: string;
  state: string;
  country: string;
  superwiser_emp_code: string;
  emergency_contact: string;
}) => {
  try {
    const response = await API.post("/customers/store", data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCustomers = async (searchText: string) => {
  try {
    const response = await API.get("/customers/getalluser", {
      params: {
        search: searchText,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCustomerByID = async (id: any) => {
  try {
    const response = await API.get(`/customers/${id}/getsingleUser`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const DeleteUser = async (id: number, isDeleted: boolean) => {
  try {
    const response = await API.patch(`/customers/${id}`, { isDeleted });
    return response;
  } catch (error) {
    console.error(error);
  }
};

export const updateCustomer = async (
  customerId: number,
  data: any,
): Promise<any> => {
  try {
    const response = await API.put(`/customers/${customerId}/update`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const activateDeactivateUser = async (
  customerId: number,
  status: boolean,
) => {
  try {
    const response = await API.put(`/customers/${customerId}/update-status`, {
      status,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Sources & Status
export interface SourcePayload {
  sourceName: string;
}

export interface SourceResponse {
  id: number;
  sourceName: string;
  slug: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LeadFromPayload {
  name: string;
}

export interface LeadFromResponse {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StatusResponse {
  id: number;
  statusName: string;
  type: string;
  slug: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StatusPayload {
  statusName: string;
  type: string;
}

export const createSource = async (
  data: SourcePayload,
): Promise<SourceResponse> => {
  try {
    const response = await API.post("/backend/sources", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSources = async (): Promise<SourceResponse[]> => {
  try {
    const response = await API.get("/backend/sources");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSourceById = async (
  id: number | string,
): Promise<SourceResponse> => {
  try {
    const response = await API.get(`/backend/sources/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateSource = async (
  id: number | string,
  data: SourcePayload,
): Promise<SourceResponse> => {
  try {
    const response = await API.put(`/backend/sources/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteSource = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/sources/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createLeadFromOption = async (
  data: LeadFromPayload,
): Promise<LeadFromResponse> => {
  try {
    const response = await API.post("/backend/lead-from-options", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getLeadFromOptions = async (): Promise<LeadFromResponse[]> => {
  try {
    const response = await API.get("/backend/lead-from-options");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateLeadFromOption = async (
  id: number | string,
  data: LeadFromPayload,
): Promise<LeadFromResponse> => {
  try {
    const response = await API.put(`/backend/lead-from-options/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteLeadFromOption = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/lead-from-options/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStatuses = async (): Promise<StatusResponse[]> => {
  try {
    const response = await API.get("/backend/status");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Companies
export interface CompanyPayload {
  id?: number;
  name: string;
  website?: string | null;
  review?: number | null;
  quotesCount?: string | null;
  isAsia?: boolean | null;
}

export interface CompanyResponse extends CompanyPayload {
  id: number;
  createdat?: string;
  updatedat?: string;
}

export const createCompany = async (
  data: CompanyPayload,
): Promise<CompanyResponse> => {
  try {
    const response = await API.post("/backend/companies", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface CompanyListResponse {
  data: CompanyResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const getCompanies = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  review?: "done" | "not_done" | "all" | string;
}): Promise<CompanyListResponse> => {
  try {
    const response = await API.get("/backend/companies", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNextCompanyId = async (): Promise<{ nextId: number }> => {
  try {
    const response = await API.get("/backend/companies/next-id");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Offices
export interface OfficePayload {
  id?: number;
  companyName?: string | null;
  officeName: string;
  address?: string | null;
  country?: string | null;
  state?: string | null;
  zipCode?: string | null;
  box?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  shippingAddress?: string | null;
  phone1?: string | null;
  phone2?: string | null;
  fax1?: string | null;
  fax2?: string | null;
  tollFreePhone?: string | null;
  email?: string | null;
  comment?: string | null;
  officeUrl?: string | null;
  officeEmail2?: string | null;
}

export interface OfficeResponse extends OfficePayload {
  id: number;
  createdat?: string;
  updatedat?: string;
}

export interface OfficeListResponse {
  data: OfficeResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const getOffices = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<OfficeListResponse> => {
  try {
    const response = await API.get("/backend/offices", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOfficeById = async (
  id: number | string,
): Promise<OfficeResponse> => {
  try {
    const response = await API.get(`/backend/offices/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getLatestOffice = async (): Promise<OfficeResponse> => {
  try {
    const response = await API.get("/backend/offices/latest");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNextOfficeId = async (): Promise<{ nextId: number }> => {
  try {
    const response = await API.get("/backend/offices/next-id");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createOffice = async (
  data: OfficePayload,
): Promise<OfficeResponse> => {
  try {
    const response = await API.post("/backend/offices", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateOffice = async (
  id: number | string,
  data: OfficePayload,
): Promise<OfficeResponse> => {
  try {
    const response = await API.put(`/backend/offices/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteOffice = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/offices/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Scorecard
export interface ScorecardPayload {
  who?: string | null;
  department?: string | null;
  measurable: string;
  notes?: string | null;
  target?: string | null;
  months?: Record<string, string>;
}

export interface ScorecardResponse extends ScorecardPayload {
  id: number;
  createdat?: string;
  updatedat?: string;
}

export interface ScorecardListResponse {
  data: ScorecardResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const getScorecards = async (params?: {
  page?: number;
  limit?: number;
}): Promise<ScorecardListResponse> => {
  try {
    const response = await API.get("/backend/scorecards", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createScorecard = async (
  data: ScorecardPayload,
): Promise<ScorecardResponse> => {
  try {
    const response = await API.post("/backend/scorecards", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateScorecard = async (
  id: number | string,
  data: ScorecardPayload,
): Promise<ScorecardResponse> => {
  try {
    const response = await API.put(`/backend/scorecards/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteScorecard = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/scorecards/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Inventory
export interface InventoryPayload {
  invid?: number;
  accessories?: string | null;
  buyer?: string | null;
  calcCert?: number | null;
  calcCertBy?: string | null;
  calcDate?: string | null;
  calcStatus?: number | null;
  calcValidDate?: string | null;
  comments?: string | null;
  condition?: string | null;
  cost?: number | null;
  datePurchased?: string | null;
  desc?: string | null;
  endUserRental?: string | null;
  functional?: number | null;
  listPrice?: number | null;
  location?: string | null;
  manu?: string | null;
  model?: string | null;
  monthlyRental?: number | null;
  opt?: string | null;
  publishControl?: number | null;
  quantity?: number | null;
  sellDealer?: number | null;
  serialNumber?: string | null;
  shipWeight?: number | null;
  status?: string | null;
  tmartPrice?: number | null;
  tmartShow?: number | null;
  tmartCountry?: string | null;
  tmartUsername?: string | null;
  tmartShipTime?: string | null;
  tmartLocationZip?: string | null;
  tmartWarrenty?: string | null;
  userManual?: number | null;
  eqCategoriesCatName?: string | null;
  eqSubCategoriesSubCatName?: string | null;
}

export interface InventoryResponse extends InventoryPayload {
  invid: number;
}

export interface InventoryListResponse {
  data: InventoryResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const getInventories = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  manu?: string;
  model?: string;
  serialNumber?: string;
  status?: string;
  location?: string;
  buyer?: string;
  opt?: string;
  desc?: string;
  accessories?: string;
  comments?: string;
  endUserRental?: string;
  eqCategoriesCatName?: string;
  eqSubCategoriesSubCatName?: string;
  listPrice?: number;
  quantity?: number;
  cost?: number;
  sellDealer?: number;
  monthlyRental?: number;
  shipWeight?: number;
  sort?: string;
  sortRules?: string;
  datePurchased?: string;
}): Promise<InventoryListResponse> => {
  try {
    const response = await API.get("/backend/inventories", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getInventoryById = async (
  id: number | string,
): Promise<InventoryResponse> => {
  try {
    const response = await API.get(`/backend/inventories/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNextInventoryId = async (): Promise<{ nextId: number }> => {
  try {
    const response = await API.get("/backend/inventories/next-id");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getLatestInventory = async (): Promise<InventoryResponse> => {
  try {
    const response = await API.get("/backend/inventories/latest");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const exportInventoryInStockExcel = async (): Promise<Blob> => {
  try {
    const response = await API.get("/backend/inventories/export-stock-only", {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const exportInventoryStockWithIncomingExcel = async (): Promise<Blob> => {
  try {
    const response = await API.get("/backend/inventories/export-stock-with-incoming", {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createInventory = async (
  data: InventoryPayload,
): Promise<InventoryResponse> => {
  try {
    const response = await API.post("/backend/inventories", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateInventory = async (
  id: number | string,
  data: InventoryPayload,
): Promise<InventoryResponse> => {
  try {
    const response = await API.put(`/backend/inventories/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteInventory = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/inventories/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCompany = async (
  id: number | string,
  data: CompanyPayload,
): Promise<CompanyResponse> => {
  try {
    const response = await API.put(`/backend/companies/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCompany = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/companies/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createStatus = async (
  data: StatusPayload,
): Promise<StatusResponse> => {
  try {
    const response = await API.post("/backend/status", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateStatus = async (
  id: number | string,
  data: StatusPayload,
): Promise<StatusResponse> => {
  try {
    const response = await API.put(`/backend/status/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteStatus = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/status/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Quotes
// Ad click ids / campaign parameters captured from the landing-page URL when a
// quote is submitted from an ad. Stored per quote on quotes_fm; null on quotes
// that did not come from an ad.
export interface AdTrackingFields {
  gclid?: string | null;
  msclkid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
}

// The uniform block the API echoes back on every quotes_fm response. Every key
// is present (null when unset), and the snake_case wire names are repeated so
// an ads integration can read back the exact shape it sent.
export interface AdTracking extends AdTrackingFields {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

export interface QuotePayload extends AdTrackingFields {
  recordId?: number;
  quoteId?: string;
  ip?: string;
  quoteSource?: string;
  assignedEmail?: string;
  createdDate?: string;
  win?: boolean;
  dollars?: number;
  quoteStatus?: string;
  subStatus?: string;
  quoteSent?: string;
  quoteSentDate?: string;
  firstFollowUpDate?: string;
  secondFollowUpDate?: string;
  finalFollowUpDate?: string;
  contactId?: number;
  contactEmail?: string;
  contactName?: string;
  phone1?: string;
  phone2?: string;
  mobile?: string;
  inConversation: boolean;
  customerComments?: string;
  companyName?: string;
  notes?: string;
  tempNum?: number;
  isDeleted?: boolean;
  items?: Array<{ eqModel?: string; [key: string]: any }>;
}

export interface QuoteResponse extends QuotePayload {
  id: number;
  adTracking?: AdTracking;
  // True when the quote carries any attribution at all.
  isAdSourced?: boolean;
}

export interface NextQuoteIdResponse {
  quoteId: string;
}

export interface QuoteNavigatorDataResponse {
  quoteIds: string[];
  notQuotedQuoteIds: string[];
  expectingPoQuoteIds: string[];
  inConversationQuoteIds?: string[];
  inConversationIds?: string[];
  counts: {
    total: number;
    open: number;
    notQuoted: number;
    expectingPo: number;
    inConversation?: number;
  };
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface QuotesListParams {
  page?: number;
  limit?: number;
  search?: string;
  [key: string]: any;
}

export interface QuotesListResponse {
  data: QuoteResponse[];
  pagination: PaginationMeta;
  summary?: {
    total: number;
    open: number;
    expectingPo: number;
    active: number;
    // How many quotes in this result set came in through an ad.
    adSourced?: number;
  };
}

export interface DashboardSummaryResponse {
  cards: {
    totalQuotesThisMonth: number;
    totalQuotesThisMonthTrend?: number;
    expectingPoAmount: number;
    expectingPoAmountThisYear?: number;
    expectingPoAmountThisYearTrend?: number;
    totalInventoryValue: number;
    totalInventoryValueAllTime?: number;
    totalSalesOrders: number;
    totalSalesOrdersThisMonth?: number;
    totalSalesOrdersThisMonthTrend?: number;
    meta?: {
      totalQuotesThisMonth?: {
        periodLabel?: string;
        compareLabel?: string;
        hasTrend?: boolean;
      };
      expectingPoAmountThisYear?: {
        periodLabel?: string;
        compareLabel?: string;
        hasTrend?: boolean;
      };
      totalInventoryValueAllTime?: {
        periodLabel?: string;
        compareLabel?: string;
        hasTrend?: boolean;
      };
      totalSalesOrdersThisMonth?: {
        periodLabel?: string;
        compareLabel?: string;
        hasTrend?: boolean;
      };
    };
    quoteTrend?: number;
  };
  monthlyRevenue: Array<{ name: string; revenue: number }>;
  statusBreakdown: Array<{ name: string; value: number; color: string }>;
  recentOrders: Array<{
    key: string;
    name: string;
    category: string;
    dateTime: string | null;
    quantity: number;
    price: string;
    status: string;
  }>;
}

export const createQuote = async (
  data: QuotePayload,
): Promise<QuoteResponse> => {
  try {
    const response = await API.post("/backend/quotes-fm", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuotes = async (
  params: QuotesListParams = {},
): Promise<QuotesListResponse> => {
  try {
    const response = await API.get("/backend/quotes-fm", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDashboardSummary = async (
  params?: { period?: "today" | "week" | "month" | "year" | string },
): Promise<DashboardSummaryResponse> => {
  try {
    const response = await API.get("/backend/dashboard/summary", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface AteDashboardSummaryResponse {
  period: string;
  range: { start: string; end: string };
  lastUpdated?: string;
  flags?: string[];
  kpis: {
    totalLeads: number;
    validEnquiries?: number;
    qualifiedLeads: number;
    qualifiedLeadsCount?: number;
    qualifiedLeadsRate?: number;
    quotations: { sent: number; pending: number; approved: number; notSentYet: number };
    closeRate: number;
    winRateOfDecided: number;
    totalRevenue: number;
    quotesOnModelsWeDontHave: number;
    totalUnitsSold: number;
    averageOrderValue: number;
    grossProfit: number;
  };
}

export const getAteDashboardSummary = async (
  params?: { period?: "today" | "week" | "month" | "year" | "all" | "custom"; startDate?: string; endDate?: string },
): Promise<AteDashboardSummaryResponse> => {
  try {
    const response = await API.get("/backend/ate-dashboard/summary", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNextQuoteId = async (): Promise<NextQuoteIdResponse> => {
  try {
    const response = await API.get("/backend/quotes-fm/next-quote-id");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuoteNavigatorData =
  async (params?: Record<string, any>): Promise<QuoteNavigatorDataResponse> => {
    try {
      const response = await API.get("/backend/quotes-fm/navigator-data", { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

export const getQuoteByQuoteId = async (
  quoteId: string,
): Promise<QuoteResponse> => {
  try {
    const response = await API.get(
      `/backend/quotes-fm/by-quoteid/${encodeURIComponent(quoteId)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateQuote = async (
  id: number | string,
  data: QuotePayload,
): Promise<QuoteResponse> => {
  try {
    const response = await API.put(`/backend/quotes-fm/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const softDeleteQuote = async (
  id: number | string,
): Promise<QuoteResponse> => {
  try {
    const response = await API.patch(`/backend/quotes-fm/${id}`, {
      isDeleted: true,
    });
    return response.data;
  } catch (error: any) {
    try {
      const fallback = await API.put(`/backend/quotes-fm/${id}`, {
        isDeleted: true,
      });
      return fallback.data;
    } catch {
      throw error;
    }
  }
};

// Quote Sequence
export interface QuoteSequencePayload {
  recordId?: number;
  createdDate?: string;
  quoteId: string;
  eqModel?: string;
  eqBrand?: string;
  options?: string;
  eqDescription?: string;
  price?: number;
  priceAutomatic?: number;
  priceString?: string;
  quantity?: number;
  wtbSentCalculated?: string;
}

export interface QuoteSequenceResponse extends QuoteSequencePayload {
  id: number;
}

export const createQuoteSequence = async (
  data: QuoteSequencePayload,
): Promise<QuoteSequenceResponse> => {
  try {
    const response = await API.post("/backend/quoteseq-fm", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuoteSequencesByQuoteId = async (
  quoteId: string,
): Promise<QuoteSequenceResponse[]> => {
  try {
    const response = await API.get(
      `/backend/quoteseq-fm/by-quoteid/${encodeURIComponent(quoteId)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuotesByModel = async (
  model: string,
): Promise<{ data: QuoteResponse[] }> => {
  try {
    const response = await API.get(
      `/backend/quoteseq-fm/by-model/${encodeURIComponent(model)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateQuoteSequence = async (
  id: number | string,
  data: QuoteSequencePayload,
): Promise<QuoteSequenceResponse> => {
  try {
    const response = await API.put(`/backend/quoteseq-fm/${id}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteQuoteSequence = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/quoteseq-fm/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface EbayItem {
  itemId: string;
  title: string;
  price: string | null;
  condition: string | null;
  imageUrl: string | null;
  itemWebUrl: string;
  seller: string | null;
  location: string | null;
}

export const searchEbayItems = async (
  query: string,
  model?: string,
  manufacturer?: string,
  location?: { country?: string; zip?: string },
): Promise<{ items: EbayItem[]; matchedQuery: string; exactMatch: boolean }> => {
  try {
    const response = await API.get(`/backend/ebay/search`, {
      params: {
        q: query,
        model,
        manufacturer,
        country: location?.country || undefined,
        zip: (location?.country && location?.zip) || undefined,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Quote Responses
export interface QuotesResponseItem {
  qid: number;
  id?: string;
  comment?: string;
  companyName?: string;
  createdDate?: string;
  demailSubject?: string;
  dStatus?: string;
  dsubstatus?: string;
  subStatus?: string;
  email?: string;
  havePO?: string;
  priceRange?: string;
  price?: number;
  priceTarget?: string;
  priceString?: string;
  qty?: string;
  eqBrand?: string;
  eqModel?: string;
  options?: string;
  sub?: string | null;
  subBrand?: string | null;
  dateQuoted?: string;
  dealerQuoted?: string;
  salesComments?: string;
  sellerType?: string;
  wtbdReplyEmail?: string;
  wtbReplyEmail?: string;
  eqBrandConsolidated?: string;
  listType?: string;
  recordId?: string;
  contactId?: number;
  contactEmail?: string;
  offices?: Array<{
    id: number;
    country?: string;
    state?: string;
    zip?: string;
    address1?: string;
    address2?: string;
    shipping?: string;
    phone1?: string;
    phone2?: string;
    fax1?: string;
    fax2?: string;
    company?: string;
    name?: string;
    email?: string;
  }>;
  contacts?: Array<{
    id: number;
    name?: string;
    email?: string;
    phone1?: string;
    phone2?: string;
    mobile?: string;
    fax?: string;
    position?: string;
    notes?: string;
  }>;
  responseOffices?: number[];
  responseContacts?: number[];
}

export interface QuotesResponsesListResponse {
  data: QuotesResponseItem[];
  pagination: PaginationMeta;
}

export const getQuotesResponses = async (params?: {
  model?: string;
  modelMatch?: "like" | "exact";
  date?: string;
  manu?: string;
  company?: string;
  options?: string;
  email?: string;
  qty?: string;
  price?: string;
  sellerType?: string;
  dStatus?: string;
  type?: string;
  listType?: string;
  page?: number;
  limit?: number;
  sort?: string;
  sortRules?: string;
  quoteId?: string;
}): Promise<QuotesResponsesListResponse> => {
  try {
    const response = await API.get("/backend/quotes-responses", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createQuotesResponse = async (
  data: Partial<QuotesResponseItem>,
): Promise<QuotesResponseItem> => {
  try {
    const response = await API.post("/backend/quotes-responses", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuotesResponseById = async (
  qid: number | string,
): Promise<QuotesResponseItem> => {
  try {
    const response = await API.get(`/backend/quotes-responses/${qid}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuotesResponsesByQuoteId = async (
  quoteId: string,
): Promise<QuotesResponseItem[]> => {
  try {
    const response = await API.get(
      `/backend/quotes-responses/by-quote/${encodeURIComponent(quoteId)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuotesResponsesNextId = async (): Promise<number> => {
  try {
    const response = await API.get("/backend/quotes-responses/next-id");
    return Number(response.data?.nextId || 1);
  } catch (error) {
    throw error;
  }
};

export const updateQuotesResponse = async (
  qid: number | string,
  data: Partial<QuotesResponseItem>,
): Promise<QuotesResponseItem> => {
  try {
    const response = await API.put(`/backend/quotes-responses/${qid}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteQuotesResponse = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/quotes-responses/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Quote WTB
export interface QuoteWtbPayload {
  id?: number | string;
  created?: string;
  createdDate?: string;
  eqBrand?: string;
  eqModel?: string;
  eqOptions?: string;
  options?: string;
  type?: string;
  sellerType?: string;
  listType?: string;
  keepAlive?: boolean | string;
  status?: string;
  priority?: number | string;
  recordId?: number | string;
  source?: string;
}

export interface QuoteWtbItem extends QuoteWtbPayload {
  id?: number | string;
  qid?: number | string;
  recordId?: number | string;
  eqBrandConsolidated?: string;
  createdText?: string;
}

export interface QuoteWtbListParams {
  page?: number;
  limit?: number;
  model?: string;
  manu?: string;
  options?: string;
  requested?: string;
  type?: string;
  listType?: string;
  status?: string;
  scope?: "Active" | "Today" | "All" | string;
  todayDate?: string;
  sortBy?: "date" | "priority" | "status" | "model" | "manufacturer" | "owner" | string;
  order?: "asc" | "desc" | string;
  sort?: string;
  sortRules?: string;
}

export interface QuoteWtbListResponse {
  data: QuoteWtbItem[];
  pagination: PaginationMeta;
}

export const createQuoteWtb = async (
  data: QuoteWtbPayload,
): Promise<QuoteWtbItem> => {
  try {
    const payload = {
      ...data,
      created: data.created || data.createdDate,
      eqOptions: data.eqOptions ?? data.options ?? "",
      type: data.type || data.listType,
      status: data.status || data.sellerType,
    };
    const response = await API.post("/backend/quoteswtb", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuoteWtb = async (
  params: QuoteWtbListParams = {},
): Promise<QuoteWtbListResponse | QuoteWtbItem[]> => {
  try {
    const response = await API.get("/backend/quoteswtb", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteQuoteWtb = async (
  id: number | string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(`/backend/quoteswtb/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateQuoteWtb = async (
  id: number | string,
  data: Partial<QuoteWtbPayload>,
): Promise<QuoteWtbItem> => {
  try {
    const payload = {
      ...data,
      created: data.created || data.createdDate,
      eqOptions: data.eqOptions ?? data.options ?? "",
      type: data.type || data.listType,
      status: data.status || data.sellerType,
    };
    const response = await API.put(`/backend/quoteswtb/${id}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface SendQuoteEmailPayload {
  quoteId?: string;
  quoteDbId?: number;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  html: string;
  fromName?: string;
}

export interface SendQuoteEmailResponse {
  message: string;
  email: {
    to: string;
    subject: string;
    messageId?: string | null;
  };
  quote?: {
    id: number;
    quoteId: string;
    quoteSent: string;
  } | null;
}

export const sendQuoteEmail = async (
  data: SendQuoteEmailPayload,
): Promise<SendQuoteEmailResponse> => {
  try {
    const response = await API.post("/backend/quote-emails/send", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface EmailTemplatePayload {
  name: string;
  description?: string;
  subjectTemplate?: string;
  bodyTemplate: string;
  senderEmail?: string;
  isActive?: boolean;
}

export interface EmailTemplateResponse extends EmailTemplatePayload {
  id: number;
  created_at?: string;
  updated_at?: string;
}

export interface EmailTemplateRenderResponse {
  template: EmailTemplateResponse;
  quote?: QuoteResponse | null;
  variables: Record<string, string>;
  rendered: {
    subject: string;
    body: string;
  };
}

export interface OutlookDraftResponse {
  message: string;
  emlFileId?: string;
  emlDownloadUrl?: string;
  rendered: {
    subject: string;
    body: string;
  };
  wtbRecordCount?: number;
}

export const getEmailTemplates = async (): Promise<EmailTemplateResponse[]> => {
  try {
    const response = await API.get("/backend/email-templates");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEmailTemplateBySlug = async (
  slug: string,
): Promise<EmailTemplateResponse> => {
  try {
    const response = await API.get(
      `/backend/email-templates/${encodeURIComponent(slug)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createEmailTemplate = async (
  data: EmailTemplatePayload,
): Promise<EmailTemplateResponse> => {
  try {
    const response = await API.post("/backend/email-templates", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateEmailTemplate = async (
  slug: string,
  data: Partial<EmailTemplatePayload>,
): Promise<EmailTemplateResponse> => {
  try {
    const response = await API.put(
      `/backend/email-templates/${encodeURIComponent(slug)}`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Absolute origin of the API host (baseURL minus its trailing /api path), so
// image URLs returned by the upload endpoint resolve from the browser too.
export const apiOrigin = (): string => {
  const base = String(process.env.REACT_APP_API_URL || "").trim();
  return base.replace(/\/api\/?$/, "").replace(/\/$/, "");
};

// Make a stored email-image path absolute for <img src> use in the editor.
export const resolveEmailImageUrl = (url: string): string => {
  const raw = String(url || "").trim();
  if (!raw || /^(https?:)?\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  return `${apiOrigin()}${raw.startsWith("/") ? "" : "/"}${raw}`;
};

export interface EmailImageUploadResponse {
  message: string;
  filename: string;
  url: string;
  size: number;
}

// Upload one image for use inside an email template body. The file is persisted
// on the server and referenced by URL, so it survives a page refresh.
export const uploadEmailImage = async (
  file: File,
): Promise<EmailImageUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    const response = await API.post("/backend/email-images", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteEmailImage = async (
  filename: string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(
      `/backend/email-images/${encodeURIComponent(filename)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteEmailTemplate = async (
  slug: string,
): Promise<{ message: string }> => {
  try {
    const response = await API.delete(
      `/backend/email-templates/${encodeURIComponent(slug)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const renderEmailTemplate = async (
  slug: string,
  quoteIdOrParams?: string | Record<string, any>,
): Promise<EmailTemplateRenderResponse> => {
  try {
    const params =
      typeof quoteIdOrParams === "string"
        ? quoteIdOrParams
          ? { quoteId: quoteIdOrParams }
          : undefined
        : quoteIdOrParams;
    const response = await API.get(
      `/backend/email-templates/render/${encodeURIComponent(slug)}`,
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createOutlookDraftFromTemplate = async (data: {
  templateSlug: string;
  quoteId?: string;
  includeWtb?: boolean;
  customItems?: Array<{ eqBrand?: string; eqModel?: string; eqOptions?: string }>;
  type?: string;
  listType?: string;
  scope?: string;
  todayDate?: string;
  status?: string;
  model?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  vendorFormUrl?: string;
  launchMode?: "desktop" | "web";
  attachments?: Array<{
    name?: string;
    fileName?: string;
    contentType?: string;
    contentBase64?: string;
  }>;
  pdfAttachment?: {
    name?: string;
    fileName?: string;
    contentType?: string;
    contentBase64?: string;
  };
  quotePdfBase64?: string;
  quotePdfFileName?: string;
}): Promise<OutlookDraftResponse> => {
  try {
    const response = await API.post(
      "/backend/outlook-drafts/from-template",
      data,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Render the template server-side and send the email directly, from the account
// picked for that template on the Email Master page (blank => default account).
// Same payload shape as createOutlookDraftFromTemplate.
export interface SendTemplateEmailResponse {
  message: string;
  to: string;
  subject: string;
  from: string;
}

export const sendTemplateEmail = async (data: {
  templateSlug: string;
  quoteId?: string;
  includeWtb?: boolean;
  customItems?: Array<{ eqBrand?: string; eqModel?: string; eqOptions?: string }>;
  type?: string;
  listType?: string;
  scope?: string;
  todayDate?: string;
  status?: string;
  model?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  fromName?: string;
  vendorFormUrl?: string;
  attachments?: Array<{
    name?: string;
    fileName?: string;
    contentType?: string;
    contentBase64?: string;
  }>;
}): Promise<SendTemplateEmailResponse> => {
  try {
    const response = await API.post("/backend/email-templates/send", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Manufacturer

export const getManufacturers = async () => {
  try {
    const response = await API.get(`/backend/manu`);
    return response.data;
  } catch (error) {
    console.error("Error fetching manufacturers:", error);
    throw error;
  }
};

export const createManufacturer = async (name: any) => {
  try {
    const response = await API.post(`/backend/manu`, {
      name,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating manufacturer:", error);
    throw error;
  }
};

export const updateManufacturer = async (id: any, name: any) => {
  try {
    const response = await API.put(`/backend/manu/${id}`, {
      name,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating manufacturer:", error);
    throw error;
  }
};

export const deleteManufacturer = async (id: any) => {
  try {
    const response = await API.delete(`/backend/manu/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting manufacturer:", error);
    throw error;
  }
};

// Vendor

export const getVendors = async () => {
  try {
    const response = await API.get(`/backend/vendors`);
    return response.data;
  } catch (error) {
    console.error("Error fetching vendors:", error);
    throw error;
  }
};

export const createVendor = async (data: any) => {
  try {
    const response = await API.post(`/backend/vendors`, data);
    return response.data;
  } catch (error) {
    console.error("Error creating vendor:", error);
    throw error;
  }
};

export const updateVendor = async (id: any, data: any) => {
  try {
    const response = await API.put(`/backend/vendors/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating vendor:", error);
    throw error;
  }
};

export const deleteVendor = async (id: any) => {
  try {
    const response = await API.delete(`/backend/vendors/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting vendor:", error);
    throw error;
  }
};

// Open order
export const createOpenOrder = async (data: any) => {
  try {
    const response = await API.post(`/backend/open-order`, data);
    return response.data;
  } catch (error) {
    console.error("Error creating open order:", error);
    throw error;
  }
};

export const getOpenOrders = async (params?: any) => {
  try {
    const response = await API.get(`/backend/open-order`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching open orders:", error);
    throw error;
  }
};

export const getOpenOrderById = async (id: any) => {
  try {
    const response = await API.get(`/backend/open-order/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching open order:", error);
    throw error;
  }
};

export const updateOpenOrder = async (id: any, data: any) => {
  try {
    const response = await API.put(`/backend/open-order/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating open order:", error);
    throw error;
  }
};

// Partial update for OOR comment fields only. Deliberately not updateOpenOrder:
// that endpoint rebuilds every column and nulls anything the body omits, so a
// single-field save through it would wipe the rest of the record.
export const updateOpenOrderComments = async (
  id: any,
  data: Record<string, string>,
) => {
  try {
    const response = await API.patch(`/backend/open-order/${id}/comments`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating open order comments:", error);
    throw error;
  }
};

export const deleteOpenOrder = async (id: any) => {
  try {
    const response = await API.delete(`/backend/open-order/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting open order:", error);
    throw error;
  }
};

// Categories
export const getCategories = async (page: number = 1) => {
  const res = await API.get(`/backend/category?page=${page}`);
  return res.data;
};

export const getCategoryById = async (id: number) => {
  const res = await API.get(`/backend/category/${id}`);
  return res.data;
};

export const createCategory = async (payload: any) => {
  const res = await API.post("/backend/category", payload);
  return res.data;
};

export const updateCategory = async (id: number, payload: any) => {
  const res = await API.put(`/backend/category/${id}`, payload);
  return res.data;
};

export const deleteCategory = async (id: number) => {
  const res = await API.delete(`/backend/category/${id}`);
  return res.data;
};

// Representative
export const createRepresentative = async (data: any) => {
  try {
    const response = await API.post(`/backend/representative`, data);
    return response.data;
  } catch (error) {
    console.error("Error creating representative:", error);
    throw error;
  }
};

export const getRepresentatives = async (page?: number, limit: number = 10) => {
  try {
    const url = page !== undefined
      ? `/backend/representative?page=${page}&limit=${limit}`
      : `/backend/representative`;
    const response = await API.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching representatives:", error);
    throw error;
  }
};

export const getRepresentativeById = async (id: any) => {
  try {
    const response = await API.get(`/backend/representative/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching representative:", error);
    throw error;
  }
};

export const updateRepresentative = async (id: any, data: any) => {
  try {
    const response = await API.put(`/backend/representative/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating representative:", error);
    throw error;
  }
};

export const deleteRepresentative = async (id: any) => {
  try {
    const response = await API.delete(`/backend/representative/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting representative:", error);
    throw error;
  }
};

// OTC Rep
export const getOtcReps = async () => {
  const response = await API.get("/backend/otc-rep");
  return response.data;
};
export const createOtcRep = async (name: string) => {
  const response = await API.post("/backend/otc-rep", { name });
  return response.data;
};
export const updateOtcRep = async (id: number, name: string) => {
  const response = await API.put(`/backend/otc-rep/${id}`, { name });
  return response.data;
};
export const deleteOtcRep = async (id: number) => {
  const response = await API.delete(`/backend/otc-rep/${id}`);
  return response.data;
};

// OTC Pmt
export const getOtcPmts = async () => {
  const response = await API.get("/backend/otc-pmt");
  return response.data;
};
export const createOtcPmt = async (name: string) => {
  const response = await API.post("/backend/otc-pmt", { name });
  return response.data;
};
export const updateOtcPmt = async (id: number, name: string) => {
  const response = await API.put(`/backend/otc-pmt/${id}`, { name });
  return response.data;
};
export const deleteOtcPmt = async (id: number) => {
  const response = await API.delete(`/backend/otc-pmt/${id}`);
  return response.data;
};

// Ship To
export const getShipTos = async () => {
  const response = await API.get("/backend/ship-to");
  return response.data;
};
export const createShipTo = async (name: string) => {
  const response = await API.post("/backend/ship-to", { name });
  return response.data;
};
export const updateShipTo = async (id: number, name: string) => {
  const response = await API.put(`/backend/ship-to/${id}`, { name });
  return response.data;
};
export const deleteShipTo = async (id: number) => {
  const response = await API.delete(`/backend/ship-to/${id}`);
  return response.data;
};

// Cal Type
export const getCalTypes = async () => {
  const response = await API.get("/backend/cal-type");
  return response.data;
};
export const createCalType = async (name: string) => {
  const response = await API.post("/backend/cal-type", { name });
  return response.data;
};
export const updateCalType = async (id: number, name: string) => {
  const response = await API.put(`/backend/cal-type/${id}`, { name });
  return response.data;
};
export const deleteCalType = async (id: number) => {
  const response = await API.delete(`/backend/cal-type/${id}`);
  return response.data;
};

// OTC SnapShot

export const getShippedOrdersByMonth = async (month: number, year: number) => {
  try {
    const response = await API.get(
      `/backend/order-tracking/shipped?month=${month}&year=${year}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching shipped orders:", error);
    throw error;
  }
};

export const getShippedOrderMonthRange = async (): Promise<{
  oldestDate: string | null;
  newestDate: string | null;
}> => {
  try {
    const response = await API.get(`/backend/order-tracking/shipped-month-range`);
    return response.data;
  } catch (error) {
    console.error("Error fetching shipped order month range:", error);
    throw error;
  }
};

export const getShippedOrdersByDateRange = async (
  startDate: string,
  endDate: string,
  extraParams: Record<string, any> = {},
) => {
  try {
    const month = dayjs(startDate).month() + 1;
    const year = dayjs(startDate).year();
    const response = await API.get(`/backend/order-tracking/shipped`, {
      params: {
        startDate,
        endDate,
        month,
        year,
        ...extraParams,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching shipped orders by date range:", error);
    throw error;
  }
};

export const getOtcBreakEvenByMonth = async (month: number, year: number) => {
  try {
    const response = await API.get(`/backend/otc/break-even`, {
      params: { month, year },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching OTC break even:", error);
    throw error;
  }
};

export const upsertOtcBreakEvenByMonth = async (
  month: number,
  year: number,
  breakEvenValue: number,
) => {
  try {
    const response = await API.put(`/backend/otc/break-even`, {
      month,
      year,
      breakEvenValue,
    });
    return response.data;
  } catch (error) {
    console.error("Error saving OTC break even:", error);
    throw error;
  }
};

// Order Tracking
export const getOrderTracking = async (params?: Record<string, any>) => {
  try {
    const response = await API.get("/backend/order-tracking", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createOrderTracking = async (data: any) => {
  try {
    const response = await API.post("/backend/order-tracking", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateOrderTracking = async (id: number | string, data: any) => {
  try {
    const response = await API.put(`/backend/order-tracking`, {
      id,
      ...data,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating order tracking:", error);
    throw error;
  }
};

export const deleteOrderTracking = async (id: number | string) => {
  try {
    const response = await API.delete("/backend/order-tracking", {
      data: { id },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrderTrackingByQuoteId = async (quoteId: string) => {
  try {
    const response = await API.get(
      `/backend/order-tracking/by-quote/${encodeURIComponent(quoteId)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// App Settings
export interface AppEmailAccount { email: string; password: string }

export const updateAppSettings = async (data: { accessToken?: string; formToken?: string; adsToken?: string; chatgptToken?: string; supportEmail?: string; supportPassword?: string; emailAccounts?: AppEmailAccount[]; emailSignature?: string }): Promise<{ message: string }> => {
  try {
    const response = await API.put("/backend/app-settings", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAppSettings = async (): Promise<{ id?: number; accessToken?: string; formToken?: string; adsToken?: string; chatgptToken?: string; supportEmail?: string; supportPassword?: string; emailAccounts?: AppEmailAccount[]; emailSignature?: string }> => {
  try {
    const response = await API.get("/backend/app-settings");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateAppToken = async (accessToken: string): Promise<{ message: string }> => {
  try {
    const response = await API.put("/backend/app-settings/token", { accessToken });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Pending external quotes (created via the form-token API, awaiting approval)
export interface PendingExternalQuoteItem {
  id: number;
  quoteId: string;
  eqBrand?: string;
  eqModel?: string;
  eqDescription?: string;
  options?: string;
  price?: number;
  priceString?: string;
  priceAutomatic?: number;
  quantity?: number;
  wtbSentCalculated?: string;
}

export interface PendingExternalQuote extends AdTrackingFields {
  id: number;
  adTracking?: AdTracking;
  isAdSourced?: boolean;
  // Blank while pending — the real id is only assigned on approval.
  quoteId?: string | null;
  // Display-only: the quoteId this quote will receive if approved now.
  previewQuoteId?: string | null;
  isPending?: boolean;
  companyName?: string;
  contactName?: string;
  contactEmail?: string;
  phone1?: string;
  phone2?: string;
  mobile?: string;
  quoteSource?: string;
  assignedEmail?: string;
  country?: string;
  customerComments?: string;
  salesInfo?: string;
  optionsRequested?: string;
  inConversation?: boolean;
  createdDate?: string;
  items: PendingExternalQuoteItem[];
}

export const getPendingExternalQuotes = async (): Promise<PendingExternalQuote[]> => {
  try {
    const response = await API.get("/backend/quotes-fm/pending-external");
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw error;
  }
};

export const approveExternalQuote = async (
  id: number,
  quoteStatus?: string,
): Promise<{
  message: string;
  id: number;
  quoteId: string;
  quoteStatus?: string | null;
  adTracking?: AdTracking;
  isAdSourced?: boolean;
}> => {
  try {
    const response = await API.put(
      `/backend/quotes-fm/${id}/approve`,
      quoteStatus ? { quoteStatus } : {},
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const rejectExternalQuote = async (id: number): Promise<{ message: string; id: number }> => {
  try {
    const response = await API.delete(`/backend/quotes-fm/${id}/reject`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface CronFollowUpSetting {
  enabled: boolean;
  daysAfter: number | null;
  runTime: string;
  templateSlug: string | null;
  skipWeekends: boolean;
}

export interface CronSettingsPayload {
  first: CronFollowUpSetting;
  second: CronFollowUpSetting;
  final: CronFollowUpSetting;
}

export const getCronSettings = async (): Promise<CronSettingsPayload> => {
  try {
    const response = await API.get("/backend/cron-settings");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const saveCronSettings = async (
  data: CronSettingsPayload,
): Promise<{ message: string }> => {
  try {
    const response = await API.put("/backend/cron-settings", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ─── AI Master (AI settings CRUD) ─────────────────────────────────────────────

export interface AiSetting {
  id?: number;
  name: string;
  provider: string;
  model: string;
  baseUrl?: string; // API endpoint / base URL; blank = provider default
  apiKey?: string; // write-only; never returned by the API
  hasApiKey?: boolean; // read-only flag from the API
  isActive: boolean;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export const getAiSettings = async (): Promise<AiSetting[]> => {
  const response = await API.get("/backend/ai-settings");
  return response.data?.data ?? [];
};

export const createAiSetting = async (
  data: Partial<AiSetting>,
): Promise<AiSetting> => {
  const response = await API.post("/backend/ai-settings", data);
  return response.data;
};

export const updateAiSetting = async (
  id: number,
  data: Partial<AiSetting>,
): Promise<AiSetting> => {
  const response = await API.put(`/backend/ai-settings/${id}`, data);
  return response.data;
};

export const deleteAiSetting = async (
  id: number,
): Promise<{ message: string }> => {
  const response = await API.delete(`/backend/ai-settings/${id}`);
  return response.data;
};

// ─── AI Model Drafts (automation review/approval) ─────────────────────────────

export interface AiModelDraft {
  id: number;
  modelNumber: string;
  manufacturer?: string | null;
  sourceSeqId?: number | null;
  aiData?: any;
  aiModelUsed?: string | null;
  status: "pending" | "approved" | "rejected" | "published" | "failed";
  origin?: string;
  rejectReason?: string | null;
  publishedPayload?: any;
  error?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const getAiModelDrafts = async (params?: {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ data: AiModelDraft[]; total: number; page: number; limit: number }> => {
  const response = await API.get("/backend/ai-model-drafts", { params });
  return response.data;
};

export const getAiModelDraft = async (id: number): Promise<AiModelDraft> => {
  const response = await API.get(`/backend/ai-model-drafts/${id}`);
  return response.data;
};

export const updateAiModelDraft = async (
  id: number,
  data: Partial<AiModelDraft>,
): Promise<AiModelDraft> => {
  const response = await API.put(`/backend/ai-model-drafts/${id}`, data);
  return response.data;
};

export const approveAiModelDraft = async (
  id: number,
  publish: boolean = true,
): Promise<AiModelDraft> => {
  const response = await API.post(`/backend/ai-model-drafts/${id}/approve`, { publish });
  return response.data;
};

export const rejectAiModelDraft = async (
  id: number,
  reason?: string,
): Promise<AiModelDraft> => {
  const response = await API.post(`/backend/ai-model-drafts/${id}/reject`, { reason });
  return response.data;
};

export const generateAiModelDraft = async (
  model: string,
  manufacturer?: string,
): Promise<{ message: string; aiUsed?: any; draft: AiModelDraft }> => {
  const response = await API.post("/backend/model-automation/generate", { model, manufacturer });
  return response.data;
};

// ─── Bulk Import ──────────────────────────────────────────────────────────────

export const bulkImportQuoteWtb = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/quoteswtb/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportQuoteResponses = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/quotes-responses/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportInventory = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/inventories/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportOpenOrders = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/open-order/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportOrderTracking = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/order-tracking/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportQuotes = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/quotes-fm/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportQuoteSeq = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/quoteseq-fm/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkImportOTC = async (data: Record<string, any>[]): Promise<{ message: string }> => {
  try {
    const response = await API.post("/backend/otc/bulk-import", { data });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ─── Dealer Quote Responses (n8n automation) ──────────────────────────────────

export interface DealerQuoteResponse {
  qid: number;
  id: string;
  email: string;
  dealerQuoted: string;
  companyName: string;
  salesComments: string;
  priceString: string | null;
  price: number | null;
  eqBrand: string;
  eqModel: string;
  options: string;
  qty: string;
  dateQuoted: string;
}

export const getPendingDealerResponses = async (): Promise<DealerQuoteResponse[]> => {
  try {
    const response = await API.get("/backend/quotes-responses/pending");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkApproveDealerResponses = async (qids: number[]): Promise<{ message: string; count: number }> => {
  try {
    const response = await API.put("/backend/quotes-responses/bulk-approve", { qids });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkRejectDealerResponses = async (qids: number[]): Promise<{ message: string; count: number }> => {
  try {
    const response = await API.delete("/backend/quotes-responses/bulk-reject", { data: { qids } });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const approveDealerResponse = async (qid: number): Promise<void> => {
  try {
    await API.put(`/backend/quotes-responses/${qid}/approve`);
  } catch (error) {
    throw error;
  }
};

export const rejectDealerResponse = async (qid: number): Promise<void> => {
  try {
    await API.delete(`/backend/quotes-responses/${qid}/reject`);
  } catch (error) {
    throw error;
  }
};

// ─── Vendor self-service response (per-link expiring token) ───────────────────

// Public axios instance — no login-token interceptor and no 401→login redirect,
// since the vendor form is opened by external users without an app account.
const PublicAPI = axios.create({ baseURL: process.env.REACT_APP_API_URL });

export interface VendorResponseItem {
  eqBrand?: string;
  eqModel?: string;
  options?: string;
  subBrand?: string;
  subModel?: string;
  comment?: string;
  price?: string;
  askingPrice?: string;
  qty?: string;
  listType?: string;
}

export interface VendorResponseCommon {
  companyName?: string;
  dealerQuoted?: string;
  email?: string;
  comment?: string;
  dStatus?: string;
  dateQuoted?: string;
  // Transient consent flag (not persisted): when true, the backend emails the
  // vendor a copy of their submitted details. Defaults to true when omitted.
  sendCopy?: boolean;
}

export interface VendorTokenContext {
  valid: boolean;
  wtbId?: string;
  brand?: string;
  model?: string;
  options?: string;
  items?: Array<{ brand?: string; model?: string; options?: string }>;
  vendorEmail?: string;
  expiresAt?: string;
}

// Admin: generate a per-link token (valid 2 months) to embed in a WTB email.
export const generateVendorToken = async (payload: {
  wtbId?: string;
  brand?: string;
  model?: string;
  options?: string;
  items?: Array<{ brand?: string; model?: string; options?: string }>;
  vendorEmail?: string;
  baseUrl?: string;
}): Promise<{ token: string; url: string; expiresAt: string }> => {
  try {
    const response = await API.post(
      "/backend/quotes-responses/vendor-token",
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Public: validate a token and fetch prefill context for the vendor form.
// Token travels as an API query param (the vendor never sees this request); the
// page URL itself keeps the token in the path. Query param avoids a CORS
// preflight that a custom header would require.
export const getVendorContext = async (
  token: string,
): Promise<VendorTokenContext> => {
  const response = await PublicAPI.get(
    "/backend/quotes-responses/vendor-context",
    { params: { token } },
  );
  return response.data;
};

// Public: submit one-or-more items; backend creates a pending record per item.
export const submitVendorResponse = async (
  token: string,
  payload: { common: VendorResponseCommon; items: VendorResponseItem[] },
): Promise<{ message: string; count: number; requestId?: string }> => {
  const response = await PublicAPI.post(
    "/backend/quotes-responses/external",
    payload,
    { params: { token } },
  );
  return response.data;
};

// Public: name-only company search for the vendor form's company-name
// matching UI (typo-assist). Guarded by the same per-link vendor token as
// getVendorContext/submitVendorResponse — returns id/name only.
export const getCompaniesPublic = async (
  token: string,
  params: { search: string; limit?: number },
): Promise<{ data: Array<{ id: number; name: string }> }> => {
  const response = await PublicAPI.get("/backend/companies/search-public", {
    params: { ...params, token },
  });
  return response.data;
};

// ─── Application logs (hidden /logs debug viewer) ─────────────────────────────

export interface AppLogEntry {
  timestamp: string | null;
  level: string;
  type?: string;
  message: string;
  context?: Record<string, any>;
  error?: {
    name?: string;
    message?: string;
    stack?: string;
    code?: string;
    dbMessage?: string;
    dbCode?: string;
    dbDetail?: string;
    dbConstraint?: string;
    validation?: Array<{ field?: string; message?: string; value?: any }>;
  };
  stack?: string;
  _source?: string;
  _unparsed?: boolean;
}

export interface AppLogsResponse {
  data: AppLogEntry[];
  total: number;
  returned: number;
  counts: Record<string, number>;
  file: {
    path: string;
    size: number;
    lastModified: string | null;
    hasPrevious: boolean;
  };
}

// Read the tail of the backend JSON Lines log. Login-guarded on the backend.
export const getAppLogs = async (params?: {
  level?: string;
  q?: string;
  stage?: string;
  requestId?: string;
  since?: string;
  limit?: number;
  includePrevious?: boolean;
}): Promise<AppLogsResponse> => {
  try {
    const response = await API.get("/backend/logs", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const clearAppLogs = async (): Promise<{ message: string }> => {
  try {
    const response = await API.delete("/backend/logs");
    return response.data;
  } catch (error) {
    throw error;
  }
};
